import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

// material-ui
import {
    Box,
    Collapse,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Typography,
    useMediaQuery,
} from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useTheme } from "@mui/material/styles";

// project imports
import NavItem from "../NavItem";

// assets
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { IconChevronDown, IconChevronUp } from "@tabler/icons";
import { useSelector } from "react-redux";
import config from "../../../../../../../../config";
import Transitions from "../../../../../widgets/extended/Transitions";

// Primero importa los componentes necesarios
import { isBrowser } from "react-device-detect";

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

const getSubtleColor = (hexColor, opacity = 0.1) => {
  if (!hexColor) return 'transparent';
  try {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } catch (error) {
    return 'transparent';
  }
};

const NavCollapse = ({ menu, level }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const leftDrawerOpened = useSelector((state) => state.session.menuVisible);
  const [anchorEl, setAnchorEl] = useState(null);
  const matchDownMd = useMediaQuery(theme.breakpoints.down("md"));

  // The drawer paper is wide only when the menu is expanded (persistent drawer
  // at md+, temporary overlay below md; the collapsed rail is desktop-only).
  // Text follows the drawer's actual width — except inside the collapsed
  // rail's hover flyout (level > 1), which always needs text.
  const showText = leftDrawerOpened || level !== 1;

  const handleClickMini = (event) => {
    setAnchorEl(null);
    if (leftDrawerOpened) {
      setOpen(!open);
      setSelected(!selected ? menu.id : null);
    } else {
      setAnchorEl(event?.currentTarget);
    }
  };

  const handleHover = (event) => {
    setAnchorEl(event?.currentTarget);
  };

  const openMini = Boolean(anchorEl);

  const handleClosePopper = () => {
    setOpen(false);
    if (!openMini) {
      if (!menu.url) {
        setSelected(null);
      }
    }
    setAnchorEl(null);
  };

  const handleClick = () => {
    setOpen(!open);
    setSelected(null);
    setSelected(!selected ? menu.id : null);
    // if (menu?.id !== 'documentsAndCampaigns') {
    //   navigate(menu.children[0]?.url);
    // }
  };

  const { pathname } = useLocation();
  const checkOpenForParent = (child, id) => {
    child.forEach((item) => {
      if (item.url === pathname) {
        setOpen(true);
        setSelected(id);
      }
    });
  };

  // menu collapse for sub-levels
  useEffect(() => {
    setOpen(false);
    setSelected(null);
    if (menu.children) {
      menu.children.forEach((item) => {
        if (item.children?.length) {
          checkOpenForParent(item.children, menu.id);
        }
        if (item.url === pathname) {
          setSelected(menu.id);
          setOpen(true);
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menu.children]);

  // menu collapse & item
  const menus = menu.children?.map((item) => {
    switch (item.type) {
      case "collapse":
        return <NavCollapse key={item.id} menu={item} level={level + 1} />;
      case "item":
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  const Icon = menu.icon;
  const menuIcon = menu.icon ? (
    menu.color ? (
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          background: getSubtleColor(menu.color, selected === menu.id ? 0.18 : 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s ease',
        }}
      >
        <Icon
          strokeWidth={1.5}
          size="1.1rem"
          style={{ color: menu.color, marginTop: "auto", marginBottom: "auto" }}
        />
      </Box>
    ) : (
      <Icon
        strokeWidth={1.5}
        size="1.3rem"
        style={{ marginTop: "auto", marginBottom: "auto" }}
      />
    )
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: selected === menu.id ? 8 : 6,
        height: selected === menu.id ? 8 : 6,
      }}
      fontSize={level > 0 ? "inherit" : "medium"}
    />
  );

  return (
    <>
      <ListItemButton
        sx={{
          borderRadius: `${config.borderRadius}px`,
          mb: 0.5,
          alignItems: leftDrawerOpened ? "flex-start" : "center",
          backgroundColor: level > 1 ? "transparent !important" : "inherit",
          py: level > 1 ? 1 : 1.25,
          pl: leftDrawerOpened ? `${level * 24}px` : 0,
          pr: leftDrawerOpened ? undefined : 0,
          justifyContent: leftDrawerOpened ? undefined : "center",
        }}
        selected={selected === menu.id}
        onClick={handleClick}
        {...(!leftDrawerOpened && {
          onMouseEnter: handleHover,
          onMouseLeave: handleClosePopper,
        })}
      >
        <ListItemIcon sx={{ my: "auto", minWidth: leftDrawerOpened ? (menu?.color ? 44 : (!menu.icon ? 18 : 36)) : 'auto' }}>
          {menuIcon}
        </ListItemIcon>
        {showText && (
          <>
            <ListItemText
              primary={
                <Typography
                  variant={selected === menu.id ? "h5" : "body1"}
                  color="inherit"
                  sx={{ my: "auto" }}
                >
                  {menu.title}
                </Typography>
              }
              secondary={
                menu.caption && (
                  <Typography
                    variant="caption"
                    sx={{ ...theme.typography.subMenuCaption }}
                    display="block"
                    gutterBottom
                  >
                    {menu.caption}
                  </Typography>
                )
              }
            />
            {open ? (
              <IconChevronUp
                stroke={1.5}
                size="1rem"
                style={{ marginTop: "auto", marginBottom: "auto" }}
              />
            ) : (
              <IconChevronDown
                stroke={1.5}
                size="1rem"
                style={{ marginTop: "auto", marginBottom: "auto" }}
              />
            )}
          </>
        )}

        {isBrowser && !leftDrawerOpened && (
          <Popper
            open={openMini}
            anchorEl={anchorEl}
            placement="right-start"
            disablePortal={false}
            modifiers={[
              {
                name: "offset",
                options: {
                  offset: [-12, 14],
                },
              },
              {
                name: "preventOverflow",
                options: {
                  boundary: "viewport",
                },
              },
            ]}
            sx={{
              overflow: "visible",
              zIndex: 2001,
              minWidth: 180,
              "&:before": {
                content: '""',
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 120,
                borderLeft: `1px solid`,
                borderBottom: `1px solid`,
                borderColor: "divider",
              },
            }}
          >
            {({ TransitionProps }) => (
              <Transitions in={openMini} {...TransitionProps}>
                <Paper
                  sx={{
                    overflow: "hidden",
                    mt: 1.5,
                    boxShadow: theme.shadows[8],
                    backgroundImage: "none",
                  }}
                >
                  <ClickAwayListener onClickAway={handleClosePopper}>
                    <Box
                      sx={{
                        py: 0.5,
                        px: 0.5,
                        minWidth: 160,
                        '& .MuiListItemButton-root': {
                          pl: '16px !important',
                          pr: '16px !important',
                          justifyContent: 'flex-start !important',
                        },
                        '& .MuiListItemIcon-root': {
                          minWidth: '36px !important',
                        },
                        '& .MuiListItemText-root': {
                          display: 'block !important',
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ px: 2, pt: 1, pb: 0.5, color: 'text.secondary', fontWeight: 600 }}
                      >
                        {menu.title}
                      </Typography>
                      {menus}
                    </Box>
                  </ClickAwayListener>
                </Paper>
              </Transitions>
            )}
          </Popper>
        )}
      </ListItemButton>
      {((isBrowser && leftDrawerOpened) ||
        (matchDownMd && leftDrawerOpened)) && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              position: "relative",
              "&:after": {
                content: "''",
                position: "absolute",
                left: "32px",
                top: 0,
                height: "100%",
                width: "1px",
                opacity: 1,
                background: theme.palette.primary.light,
              },
            }}
          >
            {menus}
          </List>
        </Collapse>
      )}
    </>
  );
};

NavCollapse.propTypes = {
  menu: PropTypes.object,
  level: PropTypes.number,
};

export default NavCollapse;
